import type { WebviewTemplateId } from '@/entities/webview-templates';
import { cn } from '@/lib/utils';
import { CheckIcon, GlobeIcon, ImageIcon, Loader2Icon, MessageSquareWarningIcon, PlayIcon, Volume2Icon } from 'lucide-react';
import type { ReactNode } from 'react';

const TEMPLATE_ICONS: Record<WebviewTemplateId, ReactNode> = {
	custom: <GlobeIcon className='size-5' />,
	photo: <ImageIcon className='size-5' />,
	video: <PlayIcon className='size-5' />,
	video_sound: <Volume2Icon className='size-5' />,
	court: <MessageSquareWarningIcon className='size-5' />,
	loading: <Loader2Icon className='size-5' />,
};

type WebviewTemplateListProps = {
	value: WebviewTemplateId | null;
	onChange: (value: WebviewTemplateId) => void;
	disabled?: boolean;
	templates: Array<{
		id: WebviewTemplateId;
		label: string;
		description: string;
		available: boolean;
	}>;
};

export default function WebviewTemplateList({ value, onChange, disabled, templates }: WebviewTemplateListProps) {
	return (
		<div className='flex flex-col gap-2'>
			{templates.map(template => {
				const selected = value === template.id;
				const isDisabled = disabled || !template.available;

				return (
					<button
						key={template.id}
						type='button'
						disabled={isDisabled}
						onClick={() => onChange(template.id)}
						className={cn(
							'relative flex w-full items-start gap-3 rounded-xl border px-3 py-3 text-left transition-colors',
							selected ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-muted/40',
							isDisabled && 'cursor-not-allowed opacity-50 hover:bg-card',
						)}
					>
						<div
							className={cn(
								'mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-lg',
								selected ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
							)}
						>
							{TEMPLATE_ICONS[template.id]}
						</div>
						<div className='min-w-0 flex-1'>
							<div className='flex items-center gap-2'>
								<span className='text-sm font-semibold'>{template.label}</span>
								{!template.available ? (
									<span className='rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground'>
										Скоро
									</span>
								) : null}
							</div>
							<p className='mt-0.5 text-xs text-muted-foreground'>{template.description}</p>
						</div>
						{selected ? <CheckIcon className='size-4 shrink-0 text-primary' /> : null}
					</button>
				);
			})}
		</div>
	);
}
